import React, { useState, useEffect } from 'react';
import { PiWarningLight } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import ExitRowDialog from '../components/ExitRowDialog';

const SeatSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { flightData, passengers = [{ name: "Jayant P." }] } = location.state || {};

  const [activeFlightTab, setActiveFlightTab] = useState(0);
  const [selectedSeats, setSelectedSeats] = useState({});
  const [totalCost, setTotalCost] = useState(0);
  const [selectedPassenger, setSelectedPassenger] = useState(0);
  const [failedImages, setFailedImages] = useState(new Set());
  const [hoveredSeat, setHoveredSeat] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showExitRowDialog, setShowExitRowDialog] = useState(false);
  const [pendingExitRowSeat, setPendingExitRowSeat] = useState(null);
  const [cabinType, setCabinType] = useState('firstClass'); // 'firstClass' or 'mainCabin'

  // Sample flight data for multi-segment flights
  const flights = [
    {
      id: 1,
      origin: "SAT",
      destination: "CLT",
      route: "SAT to CLT",
      flightNumber: "AA 1119",
      aircraft: "Boeing 737-800",
      travelTime: "3h",
      flightCount: "Flight 1 of 4"
    },
    {
      id: 2,
      origin: "CLT",
      destination: "EWR",
      route: "CLT to EWR",
      flightNumber: "AA 1120",
      aircraft: "Boeing 737-800",
      travelTime: "2h 5m",
      flightCount: "Flight 2 of 4"
    },
    {
      id: 3,
      origin: "LGA",
      destination: "DFW",
      route: "LGA to DFW",
      flightNumber: "AA 1121",
      aircraft: "Boeing 737-800",
      travelTime: "4h 15m",
      flightCount: "Flight 3 of 4"
    },
    {
      id: 4,
      origin: "DFW",
      destination: "SAT",
      route: "DFW to SAT",
      flightNumber: "AA 1122",
      aircraft: "Boeing 737-800",
      travelTime: "1h 30m",
      flightCount: "Flight 4 of 4"
    }
  ];

  // Seat map data structure with both First Class and Main Cabin
  const seatMap = {
    aircraft: "Boeing 737-800",
    travelTime: "3h",
    sections: [
      {
        name: "First Class",
        type: "firstClass",
        rows: [
          { number: 1, seats: ["1A", "1B", "", "1E", "1F"], seatTypes: { "1A": "first", "1B": "unavailable", "1E": "unavailable", "1F": "unavailable" } },
          { number: 2, seats: ["2A", "2B", "", "2E", "2F"], seatTypes: { "2A": "unavailable", "2B": "unavailable", "2E": "first", "2F": "unavailable" } },
          { number: 3, seats: ["3A", "3B", "", "3E", "3F"], seatTypes: { "3A": "unavailable", "3B": "unavailable", "3E": "unavailable", "3F": "unavailable" } }
        ]
      },
      {
        name: "Main Cabin",
        type: "mainCabin",
        rows: [
          { number: 8, seats: ["8A", "8B", "8C", "", "8D", "8E", "8F"], seatTypes: { "8A": "unavailable", "8B": "mce", "8C": "unavailable", "8D": "unavailable", "8E": "unavailable", "8F": "unavailable" } },
          { number: 9, seats: ["9A", "9B", "9C", "", "9D", "9E", "9F"], seatTypes: { "9A": "mce", "9B": "available", "9C": "unavailable", "9D": "unavailable", "9E": "unavailable", "9F": "unavailable" } },
          { number: 10, seats: ["10A", "10B", "10C", "", "10D", "10E", "10F"], seatTypes: { "10A": "unavailable", "10B": "mce", "10C": "unavailable", "10D": "mce", "10E": "preferred", "10F": "unavailable" } },
          { number: 11, seats: ["11A", "11B", "11C", "", "11D", "11E", "11F"], seatTypes: { "11A": "mce", "11B": "unavailable", "11C": "unavailable", "11D": "mce", "11E": "unavailable", "11F": "mce" } },
          { number: 12, seats: ["12A", "12B", "12C", "", "12D", "12E", "12F"], seatTypes: { "12A": "mce", "12B": "unavailable", "12C": "unavailable", "12D": "mce", "12E": "unavailable", "12F": "mce" } },
          { number: 13, seats: ["13A", "13B", "13C", "", "13D", "13E", "13F"], seatTypes: { "13A": "unavailable", "13B": "unavailable", "13C": "unavailable", "13D": "unavailable", "13E": "unavailable", "13F": "unavailable" } },
          { number: 14, seats: ["14A", "14B", "14C", "", "14D", "14E", "14F"], seatTypes: { "14A": "preferred", "14B": "unavailable", "14C": "unavailable", "14D": "unavailable", "14E": "unavailable", "14F": "preferred" } },
          { number: 15, seats: ["15A", "15B", "15C", "", "15D", "15E", "15F"], seatTypes: { "15A": "unavailable", "15B": "unavailable", "15C": "unavailable", "15D": "unavailable", "15E": "unavailable", "15F": "unavailable" } },
          { number: 16, seats: ["16A", "16B", "16C", "", "16D", "16E", "16F"], seatTypes: { "16A": "mce", "16B": "mce", "16C": "mce", "16D": "mce", "16E": "mce", "16F": "mce" }, isExitRow: true },
          { number: 17, seats: ["17A", "17B", "17C", "", "17D", "17E", "17F"], seatTypes: { "17A": "mce", "17B": "mce", "17C": "mce", "17D": "mce", "17E": "mce", "17F": "mce" }, isExitRow: true },
          { number: 18, seats: ["18A", "18B", "18C", "", "18D", "18E", "18F"], seatTypes: { "18A": "unavailable", "18B": "unavailable", "18C": "unavailable", "18D": "unavailable", "18E": "unavailable", "18F": "unavailable" }, isExitRow: true },
          { number: 19, seats: ["19A", "19B", "19C", "", "19D", "19E", "19F"], seatTypes: { "19A": "unavailable", "19B": "unavailable", "19C": "unavailable", "19D": "unavailable", "19E": "unavailable", "19F": "unavailable" } },
          { number: 20, seats: ["20A", "20B", "20C", "", "20D", "20E", "20F"], seatTypes: { "20A": "unavailable", "20B": "unavailable", "20C": "unavailable", "20D": "unavailable", "20E": "unavailable", "20F": "unavailable" } },
          { number: 21, seats: ["21A", "21B", "21C", "", "21D", "21E", "21F"], seatTypes: { "21A": "unavailable", "21B": "unavailable", "21C": "unavailable", "21D": "unavailable", "21E": "unavailable", "21F": "available" } },
          { number: 22, seats: ["22A", "22B", "22C", "", "22D", "22E", "22F"], seatTypes: { "22A": "unavailable", "22B": "unavailable", "22C": "unavailable", "22D": "unavailable", "22E": "unavailable", "22F": "unavailable" } },
          { number: 23, seats: ["23A", "23B", "23C", "", "23D", "23E", "23F"], seatTypes: { "23A": "available", "23B": "unavailable", "23C": "unavailable", "23D": "unavailable", "23E": "unavailable", "23F": "available" } },
          { number: 24, seats: ["24A", "24B", "24C", "", "24D", "24E", "24F"], seatTypes: { "24A": "unavailable", "24B": "unavailable", "24C": "unavailable", "24D": "unavailable", "24E": "unavailable", "24F": "unavailable" } },
          { number: 25, seats: ["25A", "25B", "25C", "", "25D", "25E", "25F"], seatTypes: { "25A": "unavailable", "25B": "unavailable", "25C": "unavailable", "25D": "unavailable", "25E": "unavailable", "25F": "unavailable" } },
          { number: 26, seats: ["26A", "26B", "26C", "", "26D", "26E", "26F"], seatTypes: { "26A": "unavailable", "26B": "unavailable", "26C": "unavailable", "26D": "unavailable", "26E": "unavailable", "26F": "unavailable" } },
          { number: 27, seats: ["27A", "27B", "27C", "", "27D", "27E", "27F"], seatTypes: { "27A": "unavailable", "27B": "unavailable", "27C": "unavailable", "27D": "unavailable", "27E": "unavailable", "27F": "unavailable" } },
          { number: 28, seats: ["28A", "28B", "28C", "", "28D", "28E", "28F"], seatTypes: { "28A": "unavailable", "28B": "unavailable", "28C": "unavailable", "28D": "unavailable", "28E": "available", "28F": "unavailable" } }
        ]
      }
    ]
  };

  // Get seat info from seat map data
  const getSeatInfo = (seatId) => {
    for (const section of seatMap.sections) {
      for (const row of section.rows) {
        if (row.seatTypes && row.seatTypes[seatId]) {
          return {
            type: row.seatTypes[seatId],
            section: section.name
          };
        }
      }
    }
    return { type: "available", section: "Main Cabin" };
  };

  const getSeatStatus = (seatId) => {
    const seatInfo = getSeatInfo(seatId);
    return seatInfo.type;
  };

  // Get seat pricing
  const getSeatPrice = (seatId) => {
    const status = getSeatStatus(seatId);
    const rowNumber = parseInt(seatId.match(/\d+/)[0]);
    const isExitRow = rowNumber === 16 || rowNumber === 17;

    switch (status) {
      case "first":
        return 0; // First class is typically included in ticket price
      case "mce":
        return isExitRow ? 62 : 68;
      case "preferred":
        return 27;
      default:
        return 0;
    }
  };

  // Get tooltip content based on seat type
  const getTooltipContent = (seatId) => {
    const seatInfo = getSeatInfo(seatId);
    const seatType = seatInfo.type;

    // Check if seat is in exit row (rows 16-17)
    const rowNumber = parseInt(seatId.match(/\d+/)[0]);
    const isExitRow = rowNumber === 16 || rowNumber === 17;

    if (isExitRow && seatType === 'mce') {
      return {
        title: seatId,
        category: 'Main Cabin Extra',
        features: [
          'Extra legroom (up to 6 inches)',
          'Preferred boarding with earlier overhead bin access',
          'Complimentary beer, wine and spirits',
          'Limited or no recline'
        ],
        isExitRow: true
      };
    }

    switch (seatType) {
      case 'first':
        return {
          title: seatId,
          category: 'First Class',
          features: [
            'Premium legroom and wider seats',
            'Priority boarding and baggage handling',
            'Complimentary premium dining and beverages',
            'Enhanced personal service'
          ]
        };
      case 'mce':
        return {
          title: seatId,
          category: 'Main Cabin Extra',
          features: [
            'Extra legroom (up to 6 inches)',
            'Preferred boarding with earlier overhead bin access',
            'Complimentary beer, wine and spirits'
          ]
        };
      case 'preferred':
        return {
          title: seatId,
          category: 'Preferred',
          features: [
            'Standard legroom',
            'Favorable location'
          ]
        };
      case 'available':
        return {
          title: seatId,
          category: 'Available seat',
          features: []
        };
      case 'unavailable':
        return {
          title: seatId,
          isUnavailable: true
        };
      default:
        return null;
    }
  };

  // Handle mouse enter for tooltips
  const handleSeatMouseEnter = (seatId, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top
    });
    setHoveredSeat(seatId);
  };

  // Handle mouse leave for tooltips
  const handleSeatMouseLeave = () => {
    setHoveredSeat(null);
  };

  const getSeatImage = (seatId) => {
    const status = getSeatStatus(seatId);
    const passengerKey = `${activeFlightTab}-${selectedPassenger}-${seatId}`;
    const isSelected = selectedSeats[passengerKey];

    if (isSelected) {
      return "https://www.aa.com/content/images/seatMap/seats/seat-selected@2x.png";
    }

    switch (status) {
      case "first":
        return "/content/images/seatMap/seats/seat-available@2x.png";
      case "available":
        return "https://www.aa.com/content/images/seatMap/seats/seat-available@2x.png";
      case "unavailable":
        return "https://www.aa.com/content/images/seatMap/seats/seat-unavailable@2x.png";
      case "mce":
        return "https://www.aa.com/content/images/seatMap/seats/seat-mpp-L@2x.png";
      case "preferred":
        return "https://www.aa.com/content/images/seatMap/seats/seat-mpp-P@2x.png";
      default:
        return "https://www.aa.com/content/images/seatMap/seats/seat-available@2x.png";
    }
  };

  const handleImageError = (imageSrc) => {
    setFailedImages(prev => new Set(prev).add(imageSrc));
  };

  const getFallbackSeatStyles = (seatId) => {
    let styles = "w-full h-full rounded border-2 flex items-center justify-center text-xs font-medium ";

    const status = getSeatStatus(seatId);
    const passengerKey = `${activeFlightTab}-${selectedPassenger}-${seatId}`;
    const isSelected = selectedSeats[passengerKey];

    if (isSelected) {
      styles += "bg-blue-500 border-blue-600 text-white ";
      return styles;
    }

    const rowNumber = parseInt(seatId.match(/\d+/)[0]);
    const isExitRow = rowNumber === 16 || rowNumber === 17;

    switch (status) {
      case "first":
        styles += "bg-blue-800 border-blue-900 text-white ";
        break;
      case "available":
        styles += "bg-blue-600 border-blue-700 text-white ";
        break;
      case "unavailable":
        styles += "bg-gray-300 border-gray-400 text-gray-500 ";
        break;
      case "mce":
        if (isExitRow) {
          styles += "bg-red-600 border-red-700 text-white ";
        } else {
          styles += "bg-orange-500 border-orange-600 text-white ";
        }
        break;
      case "preferred":
        styles += "bg-green-600 border-green-700 text-white ";
        break;
      default:
        styles += "bg-blue-600 border-blue-700 text-white ";
    }

    return styles;
  };

  const handleSeatClick = (seatId) => {
    const status = getSeatStatus(seatId);
    if (status === 'unavailable') {
      return;
    }

    // Check if it's an exit row seat and not already selected
    const rowNumber = parseInt(seatId.match(/\d+/)[0]);
    const isExitRow = rowNumber === 16 || rowNumber === 17;
    const seatKey = `${activeFlightTab}-${selectedPassenger}-${seatId}`;
    const isSelected = selectedSeats[seatKey];

    if (isExitRow && !isSelected) {
      // Show exit row confirmation dialog
      setPendingExitRowSeat(seatId);
      setShowExitRowDialog(true);
      return;
    }

    // Proceed with normal seat selection
    selectSeat(seatId);
  };

  const selectSeat = (seatId) => {
    const status = getSeatStatus(seatId);
    const passengerKey = `${activeFlightTab}-${selectedPassenger}`;
    const existingSeatKey = Object.keys(selectedSeats).find(key =>
      key.startsWith(passengerKey + "-")
    );

    const seatKey = `${activeFlightTab}-${selectedPassenger}-${seatId}`;
    const isSelected = selectedSeats[seatKey];
    const price = getSeatPrice(seatId);

    if (isSelected) {
      const newSeats = { ...selectedSeats };
      delete newSeats[seatKey];
      setSelectedSeats(newSeats);
      setTotalCost(prev => prev - price);
    } else {
      if (existingSeatKey) {
        const existingSeat = selectedSeats[existingSeatKey];
        const newSeats = { ...selectedSeats };
        delete newSeats[existingSeatKey];
        newSeats[seatKey] = { id: seatId, price, type: status };
        setSelectedSeats(newSeats);
        setTotalCost(prev => prev - (existingSeat.price || 0) + price);
      } else {
        setSelectedSeats(prev => ({
          ...prev,
          [seatKey]: { id: seatId, price, type: status }
        }));
        setTotalCost(prev => prev + price);
      }
    }
  };

  const handleExitRowConfirm = () => {
    if (pendingExitRowSeat) {
      selectSeat(pendingExitRowSeat);
    }
    setShowExitRowDialog(false);
    setPendingExitRowSeat(null);
  };

  const handleExitRowCancel = () => {
    setShowExitRowDialog(false);
    setPendingExitRowSeat(null);
  };

  const getSelectedSeatForPassenger = (flightIndex, passengerIndex) => {
    const seatKey = Object.keys(selectedSeats).find(key =>
      key.startsWith(`${flightIndex}-${passengerIndex}-`)
    );
    return seatKey ? selectedSeats[seatKey] : null;
  };

  const currentFlight = flights[activeFlightTab];


  // Tooltip component - matching scraped HTML structure
  const SeatTooltip = ({ seatId, position }) => {
    const tooltipContent = getTooltipContent(seatId);

    if (!tooltipContent) return null;

    const currentPassenger = passengers[selectedPassenger] || passengers[0];
    const seatPrice = getSeatPrice(seatId);

    return (
      <div
        className="fixed pointer-events-none"
        style={{
          left: `${position.x}px`,
          top: `${position.y - 10}px`,
          transform: 'translateX(-50%) translateY(-100%)',
          zIndex: 999,
          opacity: 1
        }}
      >
        {/* Main tooltip container */}
        <div
          className="bg-white text-left w-full"
          style={{
            color: '#36495a',
            border: '1px solid #4db4fa',
            fontFamily: 'Arial, sans-serif',
            lineHeight: '18px',
            fontSize: '12px',
            boxShadow: '2px 3px 4px 0 rgba(19, 19, 19, 0.3)',
            minWidth: '100px'
          }}
        >
          <div className="tooltip-wrapper">

            {/* Tooltip Title */}
            <h5
              className="tooltip-title p-3 pb-0"
              style={{
                color: '#36495a',
                fontWeight: 700,
                margin: '0 0 10px',
                fontFamily: 'Arial, sans-serif',
                fontSize: '16px',
                lineHeight: '22px'
              }}
            >
              <div className="tooltipTitle">
                {/* Exit Row Warning */}
                {tooltipContent.isExitRow && (
                  <div className="flex items-center mb-2">
                    <PiWarningLight className='w-4 h-4 mr-1' />
                    <span className="text-xs font-medium">EXIT ROW SEAT</span>
                  </div>
                )}
                {/* Seat Info */}
                <div className="seatInfo text-sm">
                  <span style={{
                    color: tooltipContent.category === 'Preferred' ? '#008712' :
                      (tooltipContent.category === 'Main Cabin Extra' || tooltipContent.isExitRow) ? '#d14904' :
                        tooltipContent.category === 'Available seat' ? '#00467f' : '#36495a'
                  }}>
                    Seat: {tooltipContent.title}
                  </span>
                  {seatPrice > 0 && (
                    <>
                      <span style={{
                        color: tooltipContent.category === 'Preferred' ? '#008712' : '#d14904'
                      }}> • </span>
                      <span className="tooltipSeatPrice font-semibold" style={{
                        color: tooltipContent.category === 'Preferred' ? '#008712' : '#d14904'
                      }}>
                        ${seatPrice}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </h5>


            {/* Tooltip Content */}
            {!tooltipContent.isUnavailable && (tooltipContent.category || (tooltipContent.features && tooltipContent.features.length > 0)) && (
              <div className="tooltipContent px-3 pb-3">
                {/* HR Divider */}
                <hr
                  className="hr-divider my-2"
                  style={{
                    border: 'none',
                    borderTop: '1px solid #e0e0e0',
                    margin: '10px 0'
                  }}
                />
                {/* Category */}
                {tooltipContent.category && (
                  <div className="cabinName font-semibold mb-2 text-base">
                    {tooltipContent.category}
                  </div>
                )}

                {/* Features */}
                {tooltipContent.features && tooltipContent.features.length > 0 && (
                  <ul className="list-basic seat-amenities space-y-1">
                    {tooltipContent.features.map((feature, index) => (
                      <li key={index} className="text-xs flex items-center gap-y-2">
                        <span className="text-[#d0dae0] mr-1">■</span>{feature}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tooltip Arrow - matching scraped CSS */}
        <div
          className="tooltip-arrow absolute"
          style={{
            borderTop: '10px solid #4db4fa',
            borderLeft: '10px solid transparent',
            borderRight: '10px solid transparent',
            left: '50%',
            bottom: '-10px',
            marginLeft: '-10px',
            width: 0,
            height: 0
          }}
        >
          <div
            className="tooltip-arrow-inner absolute"
            style={{
              borderTop: '10px solid #fff',
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              left: '-10px',
              top: '-12px',
              width: 0,
              height: 0
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <main className="container mx-auto max-w-[1100px]" role="main">
        {/* Header */}
        <div className="p-6">
          <h1 className="text-3xl font-[50] text-[#0078d2] mb-2">Choose your seat</h1>
        </div>

        {/* Flight Tabs Navigation */}
        <div className="mx-6 border-b-2 border-[#9da6ab] mb-6">
          <div className="flex px-5">
            {flights.map((flight, index) => (
              <button
                key={flight.id}
                onClick={() => setActiveFlightTab(index)}
                className={`flex-1 px-4 py-4 text-center transition-colors relative hover:underline rounded-t-lg  ${activeFlightTab === index
                  ? "text-[#00467f] bg-white border-t-[1px] border-l-[1px] border-r-[1px] border-b-0 border-[#9da6ab] -mb-px"
                  : "text-[#0078d2] "
                  }`}
              >
                <div className="font-medium">{flight.route}</div>
                <div className={`text-sm ${activeFlightTab === index ? "text-[#00467f]" : "text-[#0078d2]"
                  }`}>{flight.flightCount}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Flight Info */}
        <div className="px-6 mb-6">
          <h2 className="text-xl font-medium text-[#00467f] mb-4">
            San Antonio, TX to Charlotte, NC
          </h2>

          <ul className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
            <li className="font-thin text-[#36495a]">{currentFlight.flightNumber}</li>
            <span className="text-[#d0dae0]">■</span>
            <li className="font-thin text-[#36495a]">{currentFlight.aircraft}</li>
            <span className="text-[#d0dae0]">■</span>
            <li className="font-thin text-[#36495a]">Travel time: {currentFlight.travelTime}</li>
          </ul>

          {/* Cabin Type Selector */}
          <div className="flex space-x-4 mb-4">
            <button
              onClick={() => setCabinType('firstClass')}
              className={`px-4 py-2 rounded font-medium transition-colors ${cabinType === 'firstClass'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              First Class
            </button>
            <button
              onClick={() => setCabinType('mainCabin')}
              className={`px-4 py-2 rounded font-medium transition-colors ${cabinType === 'mainCabin'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              Main Cabin
            </button>
          </div>
        </div>

        {/* Seat Legend - Dynamic based on cabin type */}
        <section id="selectSeatLegend" className="seatLegend mb-6 px-6" tabIndex="-1">
          <h3 className="sr-only">Seat Map Legend</h3>
          <div style={{ backgroundColor: '#f5f7f7', padding: '20px 20px 0 30px' }}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {cabinType === 'firstClass' ? (
                // First Class Legend - Simplified like original
                <>
                  <div className="legend-item mb-2">
                    <div className="flex items-start space-x-3">
                      <div className="legend-image flex-shrink-0">
                        <img
                          aria-hidden="true"
                          alt=""
                          src="/content/images/seatMap/seats/seat-available@2x.png"
                          className="w-8 h-6 object-contain"
                          onError={(e) => {
                            e.target.src = "https://www.aa.com/content/images/seatMap/seats/seat-available@2x.png";
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="legend-name">
                          <div className="font-medium text-gray-900 mb-1">Available</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Unavailable */}
                  <div className="legend-item">
                    <div className="flex items-start space-x-3">
                      <div className="legend-image flex-shrink-0">
                        <img
                          aria-hidden="true"
                          alt=""
                          src="/content/images/seatMap/seats/seat-unavailable@2x.png"
                          className="w-8 h-6 object-contain"
                          onError={(e) => {
                            e.target.src = "https://www.aa.com/content/images/seatMap/seats/seat-unavailable@2x.png";
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="legend-name">
                          <div className="font-medium text-gray-900 mb-1">Unavailable</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                // Main Cabin Legend
                <>
                  {/* Main Cabin Extra */}
                  <div className="legend-item">
                    <div className="flex items-start space-x-3">
                      <div className="legend-image flex-shrink-0">
                        <img
                          aria-hidden="true"
                          alt=""
                          src="/content/images/seatMap/seats/seat-mpp-L@2x.png"
                          className="w-8 h-6 object-contain"
                          onError={(e) => {
                            e.target.src = "https://www.aa.com/content/images/seatMap/seats/seat-mpp-L@2x.png";
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="legend-name">
                          <div className="font-medium text-[#36495a] mb-1">Main Cabin Extra</div>
                          <ul className="text-xs text-[#36495a] space-y-0.5">
                            <li className="flex items-start"><span className="text-[#36495a] mr-1">■</span>Extra legroom (up to 6 inches)</li>
                            <li className="flex items-start"><span className="text-[#36495a] mr-1">■</span>Preferred boarding with earlier overhead bin access</li>
                            <li className="flex items-start"><span className="text-[#36495a] mr-1">■</span>Complimentary beer, wine and spirits</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Preferred */}
                  <div className="legend-item">
                    <div className="flex items-start space-x-3">
                      <div className="legend-image flex-shrink-0">
                        <img
                          aria-hidden="true"
                          alt=""
                          src="/content/images/seatMap/seats/seat-mpp-P@2x.png"
                          className="w-8 h-6 object-contain"
                          onError={(e) => {
                            e.target.src = "https://www.aa.com/content/images/seatMap/seats/seat-mpp-P@2x.png";
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="legend-name">
                          <div className="font-medium text-gray-900 mb-1">Preferred</div>
                          <ul className="text-xs text-gray-600 space-y-0.5">
                            <li>Standard legroom</li>
                            <li>Favorable location</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Available */}
                  <div className="legend-item">
                    <div className="flex items-start space-x-3">
                      <div className="legend-image flex-shrink-0">
                        <img
                          aria-hidden="true"
                          alt=""
                          src="/content/images/seatMap/seats/seat-available@2x.png"
                          className="w-8 h-6 object-contain"
                          onError={(e) => {
                            e.target.src = "https://www.aa.com/content/images/seatMap/seats/seat-available@2x.png";
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="legend-name">
                          <div className="font-medium text-gray-900 mb-1">Available</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Unavailable */}
                  <div className="legend-item">
                    <div className="flex items-start space-x-3">
                      <div className="legend-image flex-shrink-0">
                        <img
                          aria-hidden="true"
                          alt=""
                          src="/content/images/seatMap/seats/seat-unavailable@2x.png"
                          className="w-8 h-6 object-contain"
                          onError={(e) => {
                            e.target.src = "https://www.aa.com/content/images/seatMap/seats/seat-unavailable@2x.png";
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="legend-name">
                          <div className="font-medium text-gray-900 mb-1">Unavailable</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="flex px-6 gap-6">
          {/* Left Side - Passenger List & Total */}
          <div className="w-80 flex-shrink-0">
            <div className="affix sticky top-4">
              {/* Passenger List */}
              <section className="mb-6">
                <div className="space-y-2">
                  {passengers.map((passenger, index) => {
                    const seat = getSelectedSeatForPassenger(activeFlightTab, index);
                    return (
                      <button
                        key={index}
                        onClick={() => setSelectedPassenger(index)}
                        className={`w-full p-4 border-2 text-left transition-colors ${selectedPassenger === index
                          ? "border-blue-600 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center text-xs font-medium mr-2">1</div>
                            <span className="font-medium text-gray-800">{passenger.name}</span>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className="text-sm text-gray-600">
                              {seat ? seat.id : "--"}
                            </div>
                            <div className="text-sm font-medium">
                              <span className="text-blue-600">
                                {seat && seat.price ? `$${seat.price}` : "--"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* Total Cost */}
              <div className="border-t border-gray-200 pt-4">
                <div className='self-end text-end'>
                  <div className="text-sm text-gray-600 mb-1">Total seats (all passengers)</div>
                  <div className="text-2xl font-medium text-gray-900 mb-4">
                    <span className="text-lg">$</span>
                    <span>{totalCost}</span>
                  </div>
                </div>

                <section className="section mb-3 flex justify-end">
                  <button
                    type="submit"
                    className="w-1/2 bg-blue-600 text-white py-3 px-4 rounded font-medium hover:bg-blue-700 transition-colors"
                    onClick={() => {
                      if (activeFlightTab < flights.length - 1) {
                        setActiveFlightTab(activeFlightTab + 1);
                      } else {
                        navigate('/checkout', { state: { selectedSeats, flights, totalCost } });
                      }
                    }}
                  >
                    {activeFlightTab < flights.length - 1 ? "Next flight" : "Continue to checkout"}
                  </button>
                </section>

                <div className="text-end">
                  <a
                    href="#"
                    className="text-blue-600 underline text-sm hover:text-blue-800"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/search');
                    }}
                  >
                    Skip seats for all flights
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Seat Map */}
          <div className="flex-1">
            <div className="max-w-sm mx-auto">
              {/* Seat Map Container */}
              <section className="seat-map-container">
                <div className="seat-map-grid aircraft-E75">

                  {/* Fuselage Body - Front Service Areas - Only for First Class */}
                  {cabinType === 'firstClass' && (
                    <div className="fuselage body row-front flex items-center justify-center" style={{
                      borderLeft: '10px solid #D0DAE0',
                      borderRight: '10px solid #D0DAE0',
                      paddingLeft: '6px',
                      paddingRight: '6px',
                      maxWidth: '340px',
                      margin: '0 auto',
                    }}>
                      <div className="plane-body w-4 flex items-center justify-center">
                        <div className="img-wrapper aisleNumber">
                          <img
                            src="https://www.aa.com/content/images/seatMap/seats/exitrow-blank-80x160.png"
                            alt=""
                            className="seat-exitrow w-4 h-8 object-contain"
                            aria-hidden="true"
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-center">
                        <div className="flex items-center space-x-1">
                          <div className="">
                            <div className="img-wrapper flex items-center justify-center w-24 h-10 bg-gray-300 rounded">
                              <img
                                src="https://www.aa.com/content/images/seatMap/seat-lavatory@2x.png"
                                alt="Washroom"
                                className="w-12 h-8"
                              />

                            </div>
                          </div>

                          <div className="">
                            <div className="firstBusinessRow img-wrapper flex items-center justify-center w-4 h-10">
                              <img
                                src="https://www.aa.com/content/images/seatMap/seat-blank@2x.png"
                                alt=""
                                className="w-12 h-8"
                              />
                            </div>
                          </div>

                          <div className="span3 galley">
                            <div className="img-wrapper flex flex-col items-center justify-center w-24 h-20 bg-gray-300 rounded">
                              <img
                                src="https://www.aa.com/content/images/seatMap/seat-galley@2x.png"
                                alt="Flight attendant station"
                                className="w-8 h-8"
                              />
                              <img src="https://www.aa.com/content/images/seatMap/seat-closet@2x.png" alt="Flight attendant station" className="w-8 h-8" />

                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="plane-body  w-4 flex items-center justify-center">
                        <div className="img-wrapper aisleNumber">
                          <img
                            src="https://www.aa.com/content/images/seatMap/seats/exitrow-blank-80x160.png"
                            alt=""
                            className="seat-exitrow w-4 h-8 object-contain"
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Seat Sections - Filter by cabin type */}
                  {seatMap.sections
                    .filter(section => section.type === cabinType)
                    .map((section, sectionIndex) => (
                      <div key={sectionIndex} className="section mb-0">
                        {/* Section Header */}
                        <div className="fuselage body" style={{
                          borderLeft: section.type === 'firstClass' ? '10px solid #D0DAE0' : '10px solid #D0DAE0',
                          borderRight: section.type === 'firstClass' ? '10px solid #D0DAE0' : '10px solid #D0DAE0',
                          paddingLeft: '6px',
                          paddingRight: '6px',
                          maxWidth: section.type === 'firstClass' ? '340px' : '340px',
                          margin: '0 auto',
                          paddingTop: '0px',
                          paddingBottom: '0px',
                          minHeight: 'auto'
                        }}>
                          <div className="plane-body w-4 flex items-center justify-center">
                            <div className="img-wrapper aisleNumber">
                              <img
                                src="https://www.aa.com/content/images/seatMap/seats/exitrow-blank-80x160.png"
                                alt=""
                                className="seat-exitrow w-4 h-8 object-contain"
                                aria-hidden="true"
                              />
                            </div>
                          </div>
                          <div className=" flex items-center justify-center">
                            <div className="">
                              <h4
                                className="text-xl font-light text-[#627a88] text-center"
                                style={{
                                  fontFamily: 'AmericanSansLight, Helvetica, Arial, sans-serif',
                                  fontWeight: '300',
                                  margin: '0px 0',
                                  padding: '0px 0'
                                }}
                              >
                                {section.name}
                              </h4>
                            </div>
                          </div>
                          <div className="plane-body w-4 flex items-center justify-center">
                            <div className="img-wrapper aisleNumber">
                              <img
                                src="https://www.aa.com/content/images/seatMap/seats/exitrow-blank-80x160.png"
                                alt=""
                                className="seat-exitrow w-4 h-4 object-contain"
                                aria-hidden="true"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Seat Letter Headers */}
                        <div className="fuselage body" style={{
                          borderLeft: '10px solid #D0DAE0',
                          borderRight: '10px solid #D0DAE0',
                          paddingLeft: '6px',
                          paddingRight: '6px',
                          maxWidth: section.type === 'firstClass' ? '340px' : '340px',
                          margin: '0 auto',
                          paddingTop: '0px',
                          paddingBottom: '0px',
                          height: '10px'
                        }}>

                          <div className="flex items-center justify-center">
                            <div className="">
                              <div className="">
                                <div className="flex items-center">
                                  <div className={`text-center text-xs text-gray-500 font-medium ${section.type === 'firstClass' ? 'w-10' : 'w-8'}`}>A</div>
                                  <div className={`text-center text-xs text-gray-500 font-medium ${section.type === 'firstClass' ? 'w-10' : 'w-8'}`}>B</div>
                                  {section.type === 'mainCabin' && (
                                    <div className="w-8 text-center text-xs text-gray-500 font-medium">C</div>
                                  )}
                                  <div className={`text-center text-xs text-gray-500 ${section.type === 'firstClass' ? 'w-10' : 'w-8'}`}></div>
                                  {section.type === 'mainCabin' && (
                                    <div className="w-8 text-center text-xs text-gray-500 font-medium">D</div>
                                  )}
                                  <div className={`text-center text-xs text-gray-500 font-medium ${section.type === 'firstClass' ? 'w-10' : 'w-8'}`}>E</div>
                                  <div className={`text-center text-xs text-gray-500 font-medium ${section.type === 'firstClass' ? 'w-10' : 'w-8'}`}>F</div>
                                </div>
                              </div>
                            </div>
                          </div>

                        </div>

                        {/* Bulkhead underline */}
                        <div className="fuselage body bulk" style={{
                          borderLeft: '10px solid #D0DAE0',
                          borderRight: '10px solid #D0DAE0',
                          paddingLeft: '6px',
                          paddingRight: '6px',
                          maxWidth: section.type === 'firstClass' ? '340px' : '340px',
                          margin: '0 auto',
                          paddingTop: '0px',
                          paddingBottom: '0px',
                          marginTop: '0px',
                          height: '5px'
                        }}>

                          <div className="span7 row-class flex items-center justify-center">
                            <div className="bulk row-7 flex items-center">
                              {section.type === 'firstClass' ? (
                                // First Class bulkhead: A-B, aisle, E-F
                                <>
                                  <div className="span1"><img alt="" src="/content/images/seatMap/seat-bulkhead@2x.png" /></div>
                                  <div className="span1"><img alt="" src="/content/images/seatMap/seat-bulkhead@2x.png" /></div>
                                  <div className="span1"><img alt="" src="/content/images/seatMap/seat-bulkhead@2x.png" /></div>
                                  <div className="span1 aisle row flex items-center justify-center">
                                    <div className="firstBusinessRow img-wrapper">
                                      <img className="" alt="" src="/content/images/seatMap/seat-blank@2x.png" />
                                    </div>
                                  </div>
                                  <div className="span1"><img alt="" src="/content/images/seatMap/seat-bulkhead@2x.png" /></div>
                                  <div className="span1"><img alt="" src="/content/images/seatMap/seat-bulkhead@2x.png" /></div>
                                  <div className="span1"><img alt="" src="/content/images/seatMap/seat-bulkhead@2x.png" /></div>
                                </>
                              ) : (
                                // Main Cabin bulkhead: A-B-C, aisle, D-E-F
                                <>
                                  <div className="span1"><img alt="" src="/content/images/seatMap/seat-bulkhead@2x.png" /></div>
                                  <div className="span1"><img alt="" src="/content/images/seatMap/seat-bulkhead@2x.png" /></div>
                                  <div className="span1"><img alt="" src="/content/images/seatMap/seat-bulkhead@2x.png" /></div>
                                  <div className="span1 aisle row flex items-center justify-center">
                                    <div className="firstBusinessRow img-wrapper">
                                      <img className="" alt="" src="/content/images/seatMap/seat-blank@2x.png" />
                                    </div>
                                  </div>
                                  <div className="span1"><img alt="" src="/content/images/seatMap/seat-bulkhead@2x.png" /></div>
                                  <div className="span1"><img alt="" src="/content/images/seatMap/seat-bulkhead@2x.png" /></div>
                                  <div className="span1"><img alt="" src="/content/images/seatMap/seat-bulkhead@2x.png" /></div>
                                </>
                              )}
                            </div>
                          </div>

                        </div>


                        {section.rows.map((row, rowIndex) => {
                          if (section.type === 'firstClass') {
                            // First Class Row Rendering
                            return (
                              <div key={rowIndex} className="fuselage body row-8 firstBusinessRow" style={{
                                borderLeft: '10px solid #D0DAE0',
                                borderRight: '10px solid #D0DAE0',
                                paddingLeft: '6px',
                                paddingRight: '6px',
                                maxWidth: '340px',
                                margin: '0 auto',
                                paddingTop: '1px',
                                paddingBottom: '1px'
                              }}>
                                <div className="plane-body span0-5 w-4 flex items-center justify-center">
                                  <div className="img-wrapper aisleNumber">
                                    <img
                                      src="https://www.aa.com/content/images/seatMap/seats/exitrow-blank-80x160.png"
                                      alt=""
                                      className="seat-exitrow w-4 h-2 object-contain"
                                      aria-hidden="true"
                                    />
                                  </div>
                                </div>
                                <div className="span7 row-class flex items-center justify-center">
                                  <div className="firstBusinessRow row-7">
                                    <div className="span7">
                                      <div className="row-5 flex items-center">
                                        {row.seats.map((seat, seatIndex) => (
                                          seat === "" ? (
                                            <div key={seatIndex} className="span1 aisle flex items-center justify-center w-10 h-10">
                                              <div className="firstBusinessRow img-wrapper">
                                                <div className="aisleNumber">
                                                  <div aria-hidden="true" className="seatID text-xs text-gray-500 font-bold">{row.number}</div>
                                                </div>
                                                <img className="" alt="" src="/content/images/seatMap/seat-blank@2x.png" />
                                              </div>
                                            </div>
                                          ) : (
                                            <div key={seat} className="span1 w-10 h-10 relative">
                                              <div
                                                role="button"
                                                tabIndex={getSeatStatus(seat) === 'unavailable' ? -1 : 0}
                                                className={`firstBusinessRow seat-wrapper ${getSeatStatus(seat) === 'first' ? 'is-available' : ''} ${selectedSeats[`${activeFlightTab}-${selectedPassenger}-${seat}`] ? 'selected' : ''} w-10 h-10 cursor-pointer flex items-center justify-center`}
                                                onClick={() => handleSeatClick(seat)}
                                                onMouseEnter={(e) => handleSeatMouseEnter(seat, e)}
                                                onMouseLeave={handleSeatMouseLeave}
                                              >
                                                {failedImages.has(getSeatImage(seat)) ? (
                                                  <div className={getFallbackSeatStyles(seat)}>
                                                    {seat.slice(-1)}
                                                  </div>
                                                ) : (
                                                  <img
                                                    src={selectedSeats[`${activeFlightTab}-${selectedPassenger}-${seat}`] ? "/content/images/seatMap/seats/pax_blue1@4x.png" : getSeatImage(seat)}
                                                    alt={`Seat ${seat}`}
                                                    className="w-10 h-10 object-contain"
                                                    onError={() => handleImageError(getSeatImage(seat))}
                                                  />
                                                )}
                                              </div>
                                            </div>
                                          )
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="plane-body span0-5 w-4 flex items-center justify-center">
                                  <div className="img-wrapper aisleNumber">
                                    <img
                                      src="https://www.aa.com/content/images/seatMap/seats/exitrow-blank-80x160.png"
                                      alt=""
                                      className="seat-exitrow w-4 h-2 object-contain"
                                      aria-hidden="true"
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          } else {
                            // Main Cabin Row Rendering (existing logic)
                            const isExitRow = row.number === 16 || row.number === 17;
                            const isGapRow = row.number >= 16 && row.number <= 19;
                            const isLastBeforeGap = row.number === 15;
                            const isFirstAfterGap = row.number === 20;

                            return (
                              <div key={rowIndex}>
                                <div
                                  className={`row flex items-center justify-center mb-1 ${isExitRow ? 'exit-row relative' : ''}`}
                                  style={{
                                    borderLeft: isGapRow ? 'none' : '10px solid #D0DAE0',
                                    borderRight: isGapRow ? 'none' : '10px solid #D0DAE0',
                                    borderBottomLeftRadius: isLastBeforeGap ? '16px' : '0',
                                    borderBottomRightRadius: isLastBeforeGap ? '16px' : '0',
                                    borderTopLeftRadius: isFirstAfterGap ? '16px' : '0',
                                    borderTopRightRadius: isFirstAfterGap ? '16px' : '0',
                                    paddingLeft: '6px',
                                    paddingRight: '6px',
                                    maxWidth: '340px',
                                    margin: '0 auto'
                                  }}
                                >
                                  {/* Left exit row indicator */}
                                  {isExitRow && (
                                    <div className="plane-body row-number w-4 flex items-center justify-center">
                                      <div className="img-wrapper aisleNumber">
                                        <img
                                          src="https://www.aa.com/content/images/seatMap/seats/exitrow-left-80x160.png"
                                          alt="Exit row left"
                                          className="seat-exitrow w-4 h-8 object-contain"
                                          aria-hidden="true"
                                        />
                                      </div>
                                    </div>
                                  )}

                                  <div className="row-class flex items-center space-x-0">
                                    <div className="mce exit row-content flex items-center">
                                      {row.seats.map((seat, seatIndex) => (
                                        seat === "" ? (
                                          <div key={seatIndex} className="span1 w-8 h-8 relative">
                                            <div className="seat-bg relative w-8 h-8">
                                              {(row.number === 16 || row.number === 17) && (
                                                <img
                                                  src="https://www.aa.com/content/images/seatMap/seats/seat-exit@2x.png"
                                                  alt="Exit row background"
                                                  className="seat-exit absolute inset-0 w-8 h-8 object-cover"
                                                />
                                              )}
                                              <div className="exit-seat-wrapper relative z-10">
                                                <div className="aisleNumber flex items-center justify-center h-full">
                                                  <div className="seatID text-xs text-gray-500 font-bold" aria-hidden="true">
                                                    {row.number}
                                                  </div>
                                                </div>
                                                {(row.number === 16 || row.number === 17) && (
                                                  <img
                                                    src="https://www.aa.com/content/images/seatMap/seat-blank@2x.png"
                                                    alt=""
                                                    className="seat seat-img absolute inset-0 w-8 h-8 object-contain opacity-0"
                                                  />
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        ) : (
                                          <div key={seat} className="span1 w-8 h-8 relative">
                                            <div className="seat-bg relative w-8 h-8">
                                              {(row.number === 16 || row.number === 17) && (
                                                <img
                                                  src="https://www.aa.com/content/images/seatMap/seats/seat-exit@2x.png"
                                                  alt="Exit row background"
                                                  className="seat-exit absolute inset-0 w-8 h-8 object-cover z-0"
                                                />
                                              )}

                                              <div className={`seat-wrapper ${getSeatStatus(seat) === 'available' ? 'is-available' : ''} ${getSeatStatus(seat) === 'mce' ? 'mce-seat mce' : ''} ${isExitRow ? 'exit' : ''} relative z-10 w-6 h-6`} style={{ top: '4px', left: '4px' }}>
                                                {failedImages.has(getSeatImage(seat)) ? (
                                                  <div
                                                    className={getFallbackSeatStyles(seat)}
                                                    onClick={() => handleSeatClick(seat)}
                                                    onMouseEnter={(e) => handleSeatMouseEnter(seat, e)}
                                                    onMouseLeave={handleSeatMouseLeave}
                                                  >
                                                    {seat.slice(-1)}
                                                  </div>
                                                ) : (
                                                  <img
                                                    src={getSeatImage(seat)}
                                                    alt={`Seat ${seat}`}
                                                    className="w-6 h-6 object-contain cursor-pointer"
                                                    onClick={() => handleSeatClick(seat)}
                                                    onError={() => handleImageError(getSeatImage(seat))}
                                                    onMouseEnter={(e) => handleSeatMouseEnter(seat, e)}
                                                    onMouseLeave={handleSeatMouseLeave}
                                                  />
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        )
                                      ))}
                                    </div>
                                  </div>

                                  {/* Right exit row indicator */}
                                  {isExitRow && (
                                    <div className="plane-body row-number w-4 flex items-center justify-center">
                                      <div className="img-wrapper aisleNumber">
                                        <img
                                          src="https://www.aa.com/content/images/seatMap/seats/exitrow-right-80x160.png"
                                          alt="Exit row right"
                                          className="seat-exitrow w-4 h-8 object-contain"
                                          aria-hidden="true"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          }
                        })}
                      </div>
                    ))}

                  {/* Rear Service Areas */}
                  {
                    cabinType !== "firstClass" &&
                    <div className="service-areas" style={{
                      borderLeft: '10px solid #D0DAE0',
                      borderRight: '10px solid #D0DAE0',
                      paddingLeft: '6px',
                      paddingRight: '6px',
                      maxWidth: '340px',
                      margin: '0 auto',
                      paddingTop: '8px'
                    }}>
                      <div className="flex justify-center mb-2">
                        <div className="flex items-center space-x-2">
                          <div className="w-16 h-8 bg-slate-300 rounded flex items-center justify-center">
                            <img src="https://www.aa.com/content/images/seatMap/seat-lavatory@2x.png" alt="Washroom" className="w-6 h-6" />
                          </div>
                          <div className="w-4"></div>
                          <div className="w-16 h-8 bg-gray-300 rounded flex items-center justify-center">
                            <img src="https://www.aa.com/content/images/seatMap/seat-galley@2x.png" alt="Flight attendant station" className="w-6 h-6" />
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                </div>
              </section>
            </div>
          </div>
        </div>

        {/* Footer Link */}
        <div className="p-6 mt-8">
          <a
            href="/terms-and-conditions"
            target="_blank"
            className="text-blue-600 text-sm underline hover:text-blue-800"
          >
            Seats terms and conditions →
          </a>
        </div>
      </main>

      {/* Tooltip */}
      {hoveredSeat && (
        <SeatTooltip
          seatId={hoveredSeat}
          position={tooltipPosition}
        />
      )}

      {/* Exit Row Dialog */}
      <ExitRowDialog
        isOpen={showExitRowDialog}
        onConfirm={handleExitRowConfirm}
        onCancel={handleExitRowCancel}
        seatId={pendingExitRowSeat}
      />
    </div>
  );
};

export default SeatSelection;